import React, { useState, useEffect, useRef, useCallback } from 'react';
import { providerAPI } from '../services/api';
import { formatProviderDisplayLabel } from '../utils/providerDisplayLabel';
import './SearchableProviderSelect.css';

const SEARCH_DEBOUNCE_MS = 250;

/**
 * Searchable provider dropdown. User types to search; results show via providerAPI.search.
 * Optional typeCode filters results to that provider type (e.g. 'DR', 'AT').
 */
function SearchableProviderSelect({ value, onChange, placeholder = 'Type to search...', typeCode, disabled, id, className }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [fetchingLabel, setFetchingLabel] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const fetchLabelForId = useCallback((providerId) => {
    if (!providerId) {
      setSelectedLabel('');
      return;
    }
    setFetchingLabel(true);
    providerAPI.getById(providerId)
      .then((res) => {
        const p = res.data?.data;
        setSelectedLabel(p ? formatProviderDisplayLabel(p) : '');
      })
      .catch(() => setSelectedLabel(''))
      .finally(() => setFetchingLabel(false));
  }, []);

  useEffect(() => {
    if (value && !selectedLabel && !fetchingLabel) {
      const inResults = results.find((p) => String(p.id) === String(value));
      if (inResults) setSelectedLabel(formatProviderDisplayLabel(inResults));
      else fetchLabelForId(value);
    }
    if (!value) setSelectedLabel('');
  }, [value, results, selectedLabel, fetchingLabel, fetchLabelForId]);

  useEffect(() => {
    if (!isOpen) return;
    if (!query.trim()) {
      setResults([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      providerAPI.search(query)
        .then((res) => {
          let list = res.data?.data ?? [];
          if (typeCode) list = list.filter((p) => p.provider_type_code === typeCode);
          setResults(list);
        })
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, SEARCH_DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, isOpen, typeCode]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
    }
    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, []);

  const handleFocus = () => {
    if (disabled) return;
    setIsOpen(true);
    setQuery('');
    setResults([]);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSelect = (provider) => {
    setSelectedLabel(formatProviderDisplayLabel(provider));
    onChange(provider.id);
    setIsOpen(false);
    setQuery('');
    setResults([]);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedLabel('');
    onChange('');
    setQuery('');
    setResults([]);
  };

  const displayValue = selectedLabel || (value && fetchingLabel ? 'Loading...' : '');
  const showClear = value && !disabled;

  return (
    <div ref={wrapperRef} className={`searchable-provider-select ${className || ''}`}>
      <div
        className={`searchable-provider-select-trigger ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={handleFocus}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={id ? `${id}-listbox` : undefined}
      >
        <span className={!displayValue ? 'placeholder' : ''}>
          {displayValue || placeholder}
        </span>
        {showClear && (
          <button type="button" className="searchable-provider-select-clear" onClick={handleClear} aria-label="Clear selection">
            ×
          </button>
        )}
        <span className="searchable-provider-select-arrow">▼</span>
      </div>
      {isOpen && (
        <div className="searchable-provider-select-dropdown" id={id ? `${id}-listbox` : undefined} role="listbox">
          <input
            ref={inputRef}
            type="text"
            className="searchable-provider-select-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type name or payee..."
            autoComplete="off"
          />
          <div className="searchable-provider-select-results">
            {loading && <div className="searchable-provider-select-loading">Searching...</div>}
            {!loading && query.trim() && results.length === 0 && (
              <div className="searchable-provider-select-empty">No matches. Try a different search.</div>
            )}
            {!loading && !query.trim() && (
              <div className="searchable-provider-select-hint">Type at least one character to search.</div>
            )}
            {!loading && results.map((p) => (
              <div
                key={p.id}
                className="searchable-provider-select-option"
                onClick={() => handleSelect(p)}
                role="option"
              >
                {formatProviderDisplayLabel(p)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchableProviderSelect;
